import { CheckboxOptionType, Radio, Spin, Typography } from 'antd'
import Color from 'color'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import _ from 'lodash'
import { FC, useEffect, useMemo, useState } from 'react'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoComponentModelType } from 'types'

import {
  CriticalProfile,
  useGetCriticalProfiles,
} from './criticalProfile/criticalProfileApi'
import { CriticalProfileChart } from './criticalProfile/CriticalProfileChart'
import { CriticalProfileLegend } from './criticalProfile/CriticalProfileLegend'
import { useCriticalProfileMarkups } from './criticalProfile/criticalProfileMarkup'

export const Title = () => 'Critical Paths'
export const Width = '24rem'

const { Text } = Typography

type AxisLabelMapping = {
  x: string
  y: string
}

const DEFAULT_AXIS_LABEL_MAPPING: AxisLabelMapping = {
  x: 'X',
  y: 'Y',
}

const COMPONENT_MODEL_TYPE_TO_AXIS_LABEL_MAPPING: Record<
  GeckoComponentModelType,
  AxisLabelMapping
> = {
  [GeckoComponentModelType.VerticalTankShell]: {
    x: 'Circumference',
    y: 'Elevation',
  },
  [GeckoComponentModelType.TankFloor]: DEFAULT_AXIS_LABEL_MAPPING,
  [GeckoComponentModelType.TankFlatRoof]: DEFAULT_AXIS_LABEL_MAPPING,
  [GeckoComponentModelType.HorizontalTankShell]: {
    y: 'Circumference',
    x: 'Elevation',
  },
  [GeckoComponentModelType.PipeSection]: {
    x: 'Circumference',
    y: 'Longitude',
  },
  [GeckoComponentModelType.LegacyBoilerWalls]: { x: 'Tubes', y: 'Elevantion' },
  [GeckoComponentModelType.ShipDeck]: DEFAULT_AXIS_LABEL_MAPPING,
  [GeckoComponentModelType.LegacyCone]: {
    y: 'Circumference',
    x: 'Elevation',
  },
  [GeckoComponentModelType.Cone]: {
    y: 'Circumference',
    x: 'Elevation',
  },
  [GeckoComponentModelType.LaunchTube]: {
    y: 'Circumference',
    x: 'Elevation',
  },
  [GeckoComponentModelType.BasicShipHull]: {
    x: '',
    y: '',
  },
  [GeckoComponentModelType.Dome]: {
    x: 'Circumference',
    y: 'Elevation',
  },
  [GeckoComponentModelType.DomeWall]: {
    x: 'Circumference',
    y: 'Elevation',
  },
  [GeckoComponentModelType.VerticalTankFoundation]: {
    x: 'Circumference',
    y: 'Elevation',
  },
  [GeckoComponentModelType.FlatCircularFloor]: DEFAULT_AXIS_LABEL_MAPPING,
  [GeckoComponentModelType.MachinedForgingSimple]: {
    x: '',
    y: '',
  },
  [GeckoComponentModelType.PressureVesselShell]: {
    x: 'Circumference',
    y: 'Length',
  },
  [GeckoComponentModelType.HemisphericalHead]: {
    x: 'Circumference',
    y: 'Length',
  },
  [GeckoComponentModelType.TorisphericalHead]: {
    x: 'Circumference',
    y: 'Length',
  },
  [GeckoComponentModelType.EllipsoidalHead]: {
    x: 'Circumference',
    y: 'Length',
  },
}

const { Title: AntTitle } = Typography

function generateColors(numColors: number): string[] {
  if (numColors <= 0) {
    return []
  }
  const baseColor = new Color(CantileverTheme.colors.highVis).darken(0.2)
  return _.range(0, 360, 360 / numColors).map((hueRotate) =>
    baseColor.rotate(hueRotate).string()
  )
}

export const CardView: FC<IndicationDetailCardProps<unknown>> = () => {
  const { indication } = useIndicationData()
  const {
    isLoading: profilesLoading,
    data: { profiles: allProfiles, cdf },
  } = useGetCriticalProfiles(indication?.id)

  const [selectedIndexAxis, setSelectedIndexAxis] = useState<'x' | 'y'>('y')
  const profileIndexAxes: ReadonlySet<CriticalProfile['index_axis']> = useMemo(
    () => new Set(allProfiles.map((p) => p.index_axis)),
    [allProfiles]
  )
  useEffect(() => {
    if (!profileIndexAxes.has('x')) {
      setSelectedIndexAxis('x')
    } else {
      setSelectedIndexAxis('y')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [[...profileIndexAxes].join('~')])

  const { relevantThicknessCDF, assetModel } = useIndicationData()
  const labelMapping: AxisLabelMapping = useMemo(() => {
    if (assetModel) {
      const modelComponents = [...assetModel.data.components.values()]
      const componentId = relevantThicknessCDF?.tags.component
      const componentModel = componentId
        ? assetModel.data.components.get(componentId)
        : _.first(modelComponents)
      return (
        (componentModel
          ? COMPONENT_MODEL_TYPE_TO_AXIS_LABEL_MAPPING[componentModel.type]
          : undefined) ?? DEFAULT_AXIS_LABEL_MAPPING
      )
    }
    return DEFAULT_AXIS_LABEL_MAPPING
  }, [assetModel, relevantThicknessCDF?.tags.component])
  const profileAxisSelectButtonOptions = useMemo<
    readonly CheckboxOptionType[]
  >(() => {
    return (
      ['y', 'x'].filter((val) =>
        profileIndexAxes.has(val as CriticalProfile['index_axis'])
      ) as CriticalProfile['index_axis'][]
    ).map((axis) => ({
      value: axis,
      label: axis ? labelMapping[axis] : 'undefined',
    }))
  }, [labelMapping, profileIndexAxes])

  const profiles = useMemo(
    () => allProfiles.filter((p) => p.index_axis === selectedIndexAxis),
    [allProfiles, selectedIndexAxis]
  )
  const colors = useMemo(
    () => generateColors(profiles?.length ?? 0),
    [profiles?.length]
  )

  const colorMap = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        profiles.map((profile, idx) => [profile.id, colors[idx]])
      ),
    [colors, profiles]
  )

  const [enabledProfileIds, setEnabledProfileIds] = useState<
    ReadonlySet<string>
  >(new Set())
  useEffect(() => {
    setEnabledProfileIds(new Set(profiles.map((profile) => profile.id)))
  }, [profiles])

  useCriticalProfileMarkups(profiles, indication?.id, colorMap)

  return (
    <div>
      <AntTitle level={3}>Critical Profiles</AntTitle>
      <Spin spinning={profilesLoading}>
        <div>
          <Text>Index axis: </Text>
          <Radio.Group
            options={[...profileAxisSelectButtonOptions]}
            onChange={({ target: { value } }) => setSelectedIndexAxis(value)}
            value={selectedIndexAxis}
            optionType='button'
            size='small'
            buttonStyle='solid'
          />
        </div>
        <CriticalProfileChart
          cdf={cdf}
          colors={colorMap}
          profiles={profiles.filter((profile) =>
            enabledProfileIds.has(profile.id)
          )}
          selection={indication?.selection}
        />
        <CriticalProfileLegend
          profiles={profiles}
          colors={colorMap}
          enabledIds={enabledProfileIds}
          setEnabledIds={setEnabledProfileIds}
        />
      </Spin>
    </div>
  )
}
